/**
 * Firebase Storage Integration for What To Eat?
 *
 * Initializes Firebase Storage and provides photo upload/download helpers.
 * Photos are stored in Firebase Storage and only the download URLs are
 * persisted in Firestore (instead of base64 data URI strings).
 *
 * Storage bucket: what-to-eat-e5ce9.firebasestorage.app
 *
 * This file is an ES module and MUST be loaded with:
 *   <script type="module" src="firebase-storage-integration.js"></script>
 */

import { initializeApp, getApps } from "https://www.gstatic.com/firebasejs/12.18.0/firebase-app.js";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-storage.js";

let storageInstance = null;
let storageReady = false;

function getConfigFromGlobal() {
  if (!globalThis.firebaseConfig) throw new Error("firebaseConfig not found on window/globalThis");
  return globalThis.firebaseConfig;
}

async function ensureStorageInitialized() {
  if (storageReady && storageInstance) return storageInstance;

  // If the app was already initialized by firebase-sdk-bootstrap.js, reuse it
  const apps = getApps();
  let app = apps.length > 0 ? apps[0] : null;

  if (!app) {
    const config = getConfigFromGlobal();
    app = initializeApp(config);
  }

  storageInstance = getStorage(app);
  storageReady = true;

  // Expose globally for non-module scripts (app.js, etc.)
  globalThis.firebaseStorageInstance = storageInstance;
  globalThis.firebaseStorageReady = true;

  // Expose helper functions globally
  globalThis.firebaseUploadPhoto = uploadPhotoToStorage;
  globalThis.firebaseGetPhotoURL = getPhotoDownloadURL;
  globalThis.firebaseDeletePhoto = deletePhotoFromStorage;
  globalThis.firebaseConvertBase64ToBlob = convertBase64ToBlob;

  console.log("[Firebase Storage] Initialized and ready");
  console.log("[Firebase Storage] Bucket:", storageInstance.app.options.storageBucket);

  return storageInstance;
}

/**
 * Convert a base64 data URI string to a Blob (for upload).
 * Example input: "data:image/png;base64,iVBORw0KGgo..."
 * Example input: "iVBORw0KGgo..." (raw base64 without header)
 */
function convertBase64ToBlob(base64Data, contentType) {
  if (!base64Data) return null;

  let mimeType = contentType || "image/jpeg";
  let base64String = base64Data;

  // Handle data URI format: data:image/png;base64,.....
  if (base64Data.startsWith("data:")) {
    const matches = base64Data.match(/^data:([^;,]+);base64,(.+)$/);
    if (matches) {
      mimeType = matches[1];
      base64String = matches[2];
    } else {
      console.warn("[Firebase Storage] Invalid data URI format, treating as raw base64");
    }
  }

  try {
    // Decode base64 to binary
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  } catch (e) {
    console.error("[Firebase Storage] Failed to convert base64 to Blob:", e.message);
    return null;
  }
}

/**
 * Upload a photo to Firebase Storage.
 *
 * @param {Object} options
 * @param {string} options.base64Data - Base64 data URI or raw base64 string of the image
 * @param {string} options.category - 'menu-items' | 'client-avatars' | 'provider-logos' | 'receipts'
 * @param {string} options.ownerId - Firebase Auth UID (client or provider)
 * @param {string} options.itemId - Menu item ID (for menu-items) or order ID (for receipts)
 * @param {string} [options.fileName] - Optional custom file name (defaults to timestamp-based)
 * @param {string} [options.contentType] - Optional MIME type (defaults to image/jpeg)
 *
 * @returns {Promise<{ downloadURL: string, storagePath: string, metadata: Object }>}
 */
async function uploadPhotoToStorage({
  base64Data,
  category,
  ownerId,
  itemId,
  fileName,
  contentType
}) {
  if (!(await ensureStorageInitialized())) {
    throw new Error("[Firebase Storage] Storage not initialized");
  }

  if (!base64Data) throw new Error("[Firebase Storage] base64Data is required");
  if (!category) throw new Error("[Firebase Storage] category is required");
  if (!ownerId) throw new Error("[Firebase Storage] ownerId (auth UID) is required");

  const VALID_CATEGORIES = ["menu-items", "client-avatars", "provider-logos", "receipts"];
  if (!VALID_CATEGORIES.includes(category)) {
    throw new Error(`[Firebase Storage] Invalid category: ${category}. Must be one of: ${VALID_CATEGORIES.join(", ")}`);
  }

  const blob = convertBase64ToBlob(base64Data, contentType);
  if (!blob) {
    throw new Error("[Firebase Storage] Failed to convert image data to Blob");
  }

  // Validate image size (max 5MB)
  if (blob.size > 5 * 1024 * 1024) {
    throw new Error("[Firebase Storage] Image exceeds 5MB maximum size");
  }

  // Build path
  const safeOwnerId = String(ownerId).replace(/[^a-zA-Z0-9_-]/g, "");
  const safeItemId = itemId ? String(itemId).replace(/[^a-zA-Z0-9_-]/g, "") : Date.now();
  const ext = (blob.type === "image/png") ? "png" :
              (blob.type === "image/webp") ? "webp" :
              (blob.type === "image/gif") ? "gif" : "jpg";
  const timestamp = Date.now();
  const safeFileName = fileName
    ? String(fileName).replace(/[^a-zA-Z0-9._-]/g, "")
    : `photo-${timestamp}.${ext}`;

  const storagePath = `${category}/${safeOwnerId}/${safeItemId}/${safeFileName}`;

  console.log("[Firebase Storage] Uploading to:", storagePath, "size:", blob.size, "type:", blob.type);

  // Upload to Storage
  const storageRef = ref(storageInstance, storagePath);
  const snapshot = await uploadBytes(storageRef, blob, {
    contentType: blob.type || "image/jpeg"
  });

  // Get the download URL
  const downloadURL = await getDownloadURL(snapshot.ref);

  const result = {
    downloadURL,
    storagePath,
    metadata: {
      name: snapshot.metadata.name,
      size: snapshot.metadata.size,
      contentType: snapshot.metadata.contentType,
      timeCreated: snapshot.metadata.timeCreated,
      updated: snapshot.metadata.updated
    }
  };

  console.log("[Firebase Storage] Upload complete:", result);

  return result;
}

/**
 * Get the download URL for a storage path.
 *
 * @param {string} storagePath - e.g. "menu-items/{providerUid}/{menuItemId}/photo-123.jpg"
 * @returns {Promise<string>} Download URL
 */
async function getPhotoDownloadURL(storagePath) {
  if (!(await ensureStorageInitialized())) {
    throw new Error("[Firebase Storage] Storage not initialized");
  }

  if (!storagePath) throw new Error("[Firebase Storage] storagePath is required");

  const storageRef = ref(storageInstance, storagePath);
  return getDownloadURL(storageRef);
}

/**
 * Delete a photo from Firebase Storage.
 *
 * @param {string} storagePath - e.g. "menu-items/{providerUid}/{menuItemId}/photo-123.jpg"
 * @returns {Promise<void>}
 */
async function deletePhotoFromStorage(storagePath) {
  if (!(await ensureStorageInitialized())) {
    throw new Error("[Firebase Storage] Storage not initialized");
  }

  if (!storagePath) throw new Error("[Firebase Storage] storagePath is required");

  const storageRef = ref(storageInstance, storagePath);
  await deleteObject(storageRef);
  console.log("[Firebase Storage] Deleted:", storagePath);
}

/**
 * List all files in a storage directory.
 *
 * @param {string} directoryPath - e.g. "menu-items/{providerUid}/{menuItemId}"
 * @returns {Promise<Array<string>>} List of file paths
 */
async function listPhotoFiles(directoryPath) {
  if (!(await ensureStorageInitialized())) {
    throw new Error("[Firebase Storage] Storage not initialized");
  }

  if (!directoryPath) throw new Error("[Firebase Storage] directoryPath is required");

  const directoryRef = ref(storageInstance, directoryPath);
  const result = await listAll(directoryRef);

  const paths = [];
  result.items.forEach((item) => paths.push(item.fullPath));

  return paths;
}

/**
 * Legacy compatibility wrapper.
 * Uploads a base64 image to Storage and returns the download URL.
 * Used by app.js for menu item photo uploads.
 *
 * @param {string} base64Data - Base64 data URI of the image
 * @param {string} providerUid - Firebase Auth UID of the provider
 * @param {string} [menuItemId] - Menu item ID
 * @returns {Promise<string>} Download URL
 */
async function legacyUploadMenuPhoto(base64Data, providerUid, menuItemId) {
  const result = await uploadPhotoToStorage({
    base64Data,
    category: "menu-items",
    ownerId: providerUid,
    itemId: menuItemId || Date.now(),
    contentType: "image/jpeg"
  });
  return result.downloadURL;
}

// Legacy global wrapper for app.js
globalThis.uploadMenuPhotoToStorage = async (base64Data, providerUid, menuItemId) => {
  return legacyUploadMenuPhoto(base64Data, providerUid, menuItemId);
};

// Auto-initialize when module loads
await ensureStorageInitialized();

console.log("[Firebase Storage] Integration module loaded");
